/**
 * Каталог электрики: розетки (и выводы под технику), слаботочка, выключатели, распредкоробка, щиток.
 * Светильники — отдельный раздел «Освещение», хотя ставятся в тот же слой.
 *
 * Вид элемента задан енамом `kind` — по нему холст выбирает компонент подложки,
 * а логика проводки понимает, где выключатель и где светильник. Символ на плане
 * рисует компонент раздела (`widgets/catalog/ui/electrical/2d/symbols`), объём —
 * модель кодом из `widgets/catalog/ui/electrical/3d`. Id элементов — ключи ссылок из слоя и реестра символов,
 * менять их нельзя без миграции сохранённых проектов.
 */
import { LayerKind } from "@/shared/plan/layerKind";
import { CatalogAttach, type CatalogSection } from "../../types";
import { ElectricalKind, type ElectricalCatalogItem } from "./types";

/** Идентификатор распределительной коробки. */
export const JUNCTION_BOX_ID = "junction-box";

/**
 * Цвет изделий в 3D — белый пластик, как у настоящих розеток и выключателей;
 * распредкоробка — серая. Цвет на плане (`color`) остаётся своим у каждого типа:
 * по нему розетку отличают от выключателя на чертеже.
 */
const WHITE_PLASTIC = "#f4f4f1";
const GREY_PLASTIC = "#d9dcdf";

export const electricalSection: CatalogSection<ElectricalCatalogItem> = {
	kind: LayerKind.Electrical,
	label: "Электрика",
	canChangeDimensions: false,
	subGroups: ["Розетки", "Слаботочка", "Выключатели", "Распредкоробки", "Щиток"],
	families: [{ id: "socket-block", name: "Розетка", defaultItem: "socket" }],
	items: [
		{
			id: "socket",
			family: "socket-block",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			name: "Розетка",
			description: "Базовая одинарная розетка для стенной установки.",
			width: 80,
			height: 80,
			depth: 16,
			elevation: 300,
			color: "#c2410c",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket",
				width: 80,
				height: 80,
				depth: 16,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "socket-double",
			family: "socket-block",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			name: "Двойная розетка",
			description: "Двойной блок розеток для кухонь и мест с повышенной нагрузкой.",
			width: 152,
			height: 80,
			depth: 16,
			elevation: 300,
			color: "#c2410c",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket",
				width: 152,
				height: 80,
				depth: 16,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "socket-power",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			name: "Силовая розетка",
			description: "Розетка для мощных приборов и силового питания.",
			width: 100,
			height: 100,
			depth: 20,
			elevation: 600,
			color: "#b91c1c",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket-power",
				width: 100,
				height: 100,
				depth: 20,
				color: WHITE_PLASTIC,
			},
		},
		...(
			[
				{ id: "socket-triple", name: "Тройная розетка", width: 224 },
				{ id: "socket-quadruple", name: "Четверная розетка", width: 296 },
				{ id: "socket-quintuple", name: "Пятерная розетка", width: 368 },
			] as const
		).map(({ id, name, width }): ElectricalCatalogItem => ({
			id,
			name,
			width,
			family: "socket-block",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			description: "Блок обычных розеток в общей рамке.",
			height: 80,
			depth: 16,
			elevation: 300,
			color: "#c2410c",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket",
				width,
				height: 80,
				depth: 16,
				color: WHITE_PLASTIC,
			},
		})),
		{
			id: "socket-usb",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			name: "USB-розетка",
			description: "Сетевая USB-зарядка в настенной рамке.",
			width: 80,
			height: 80,
			depth: 16,
			elevation: 300,
			color: "#0e7490",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket-usb",
				width: 80,
				height: 80,
				depth: 16,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "socket-three-phase",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			name: "Трёхфазная розетка",
			description: "Розетка трёхфазного питания. Тип и характеристики выбираются под оборудование.",
			width: 100,
			height: 100,
			depth: 20,
			elevation: 600,
			color: "#be123c",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket-power",
				width: 100,
				height: 100,
				depth: 20,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "socket-ac",
			kind: ElectricalKind.Socket,
			subGroup: "Розетки",
			name: "Вывод под кондиционер",
			description:
				"Кабельный вывод для внутреннего блока сплит-системы - рядом с блоком, под потолком. Ставится сам, если в режиме «Кабель» кликнуть по кондиционеру.",
			width: 80,
			height: 80,
			depth: 16,
			elevation: 2200,
			color: "#0e7490",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket-power",
				width: 80,
				height: 80,
				depth: 16,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "socket-tv",
			kind: ElectricalKind.LowVoltage,
			subGroup: "Слаботочка",
			name: "ТВ-розетка",
			description: "Розетка для телевизионного сигнала и коаксиального кабеля.",
			width: 80,
			height: 80,
			depth: 12,
			elevation: 300,
			color: "#7c3aed",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket-tv",
				width: 80,
				height: 80,
				depth: 12,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "socket-net",
			kind: ElectricalKind.LowVoltage,
			subGroup: "Слаботочка",
			name: "Интернет",
			description: "Розетка для доступа в локальную сеть, LAN и интернет.",
			width: 80,
			height: 80,
			depth: 12,
			elevation: 300,
			color: "#7c3aed",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "socket-net",
				width: 80,
				height: 80,
				depth: 12,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "switch",
			kind: ElectricalKind.Switch,
			subGroup: "Выключатели",
			name: "Выключатель",
			description: "Одиночный выключатель для освещения.",
			width: 80,
			height: 80,
			depth: 12,
			elevation: 900,
			color: "#1d4ed8",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "switch",
				width: 80,
				height: 80,
				depth: 12,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "switch-double",
			kind: ElectricalKind.Switch,
			subGroup: "Выключатели",
			name: "Двойной выключатель",
			description: "Двойной выключатель для управляемых групп освещения.",
			width: 152,
			height: 80,
			depth: 12,
			elevation: 900,
			color: "#1d4ed8",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "switch",
				width: 152,
				height: 80,
				depth: 12,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "switch-triple",
			kind: ElectricalKind.Switch,
			subGroup: "Выключатели",
			name: "Трёхклавишный выключатель",
			description: "Выключатель для трёх групп освещения.",
			width: 224,
			height: 80,
			depth: 12,
			elevation: 900,
			color: "#1d4ed8",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "switch",
				width: 224,
				height: 80,
				depth: 12,
				color: WHITE_PLASTIC,
			},
		},
		{
			id: "junction-box",
			kind: ElectricalKind.JunctionBox,
			subGroup: "Распредкоробки",
			name: "Распределительная коробка",
			description:
				"Точка соединения и разветвления кабелей внутри группы питания. Расположение зависит от схемы проводки.",
			width: 120,
			height: 120,
			depth: 45,
			elevation: 2500,
			color: "#334155",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "junction-box",
				width: 120,
				height: 120,
				depth: 45,
				color: GREY_PLASTIC,
			},
		},
		{
			id: "ceiling-junction-box",
			kind: ElectricalKind.JunctionBox,
			subGroup: "Распредкоробки",
			name: "Потолочная распредкоробка",
			description: "Коробка для разветвления кабелей на потолке.",
			width: 120,
			height: 120,
			depth: 45,
			elevation: 0,
			color: "#334155",
			attach: CatalogAttach.Ceiling,
			view3d: { type: "box", width: 120, height: 45, depth: 120, color: GREY_PLASTIC },
		},
		{
			id: "low-voltage-panel",
			kind: ElectricalKind.LowVoltagePanel,
			subGroup: "Слаботочка",
			name: "Слаботочный узел",
			description: "Узел сети и телевидения: место роутера, коммутатора или распределения ТВ.",
			width: 300,
			height: 400,
			depth: 120,
			elevation: 1700,
			color: "#7c3aed",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "junction-box",
				width: 300,
				height: 400,
				depth: 120,
				color: GREY_PLASTIC,
			},
		},
		{
			id: "electrical-panel",
			kind: ElectricalKind.Panel,
			subGroup: "Щиток",
			name: "Электрощит",
			description:
				"Квартирный щиток с автоматами: от него идут линии к распредкоробкам и силовым выводам - плите, кондиционеру, бойлеру. Обычно в прихожей, низ на 1500 мм.",
			width: 300,
			height: 400,
			depth: 120,
			elevation: 1700,
			color: "#1e293b",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "junction-box",
				width: 300,
				height: 400,
				depth: 120,
				color: GREY_PLASTIC,
			},
		},
	],
};
